import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CircularProgress, Box, CssBaseline } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import MessagesView from './components/MessagesView';
import ZendeskView from './components/ZendeskView';
import VectorSearch from './components/VectorSearch';
import theme from './theme';

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, checkingAuth } = useAuth();

  if (checkingAuth) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress sx={{ color: theme.palette.primary.main }} />
      </Box>
    );
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

/**
 * App: Root component providing theming, date localization, auth, and routing.
 *
 * Purpose: Centralizes providers and routes. We intentionally delay the initial
 * render until after the component is mounted in the browser. This avoids early
 * layout measurements by MUI components (e.g., ScrollbarSize in Tabs/Select)
 * before refs are attached or stylesheets are fully applied, which previously
 * caused runtime errors like "offsetHeight of null" and a flash of unstyled
 * content (FOUC).
 */
const App: React.FC = () => {
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setHydrated(true);
  }, []);

  if (!hydrated) {
    return null;
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <AuthProvider>
          <Router>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route
                path="/messages"
                element={
                  <PrivateRoute>
                    <MessagesView />
                  </PrivateRoute>
                }
              />
              <Route
                path="/zendesk"
                element={
                  <PrivateRoute>
                    <ZendeskView />
                  </PrivateRoute>
                }
              />
              <Route
                path="/vector"
                element={
                  <PrivateRoute>
                    <VectorSearch />
                  </PrivateRoute>
                }
              />
              <Route path="/" element={<Navigate to="/zendesk" />} />
            </Routes>
          </Router>
        </AuthProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

export default App;
